import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatCurrency, formatNumberWithDecimal, getCurrentFinancialYear } from "../../../utils/commonUtil";
import { REIButton } from "../../../components/ui";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import EditExpense from "./EditExpense";
import { useNavigate } from "react-router-dom";
import { useFilterSelection } from "../FilterSelectionContext";

interface ICashFlowExpense {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  type: string;
  propertyManagment: number;
  serviceContranct: number;
  rates: number;
  bodyCorp: number;
  insurance: number;
  repairs: number;
  landLease: number;
  garden: number;
  cleaning: number;
  other: number;
}

interface ICashFlowExpenseSummary {
  type: string;
  propertyManagment: number;
  serviceContranct: number;
  rates: number;
  bodyCorp: number;
  insurance: number;
  repairs: number;
  landLease: number;
  garden: number;
  cleaning: number;
  other: number;
}

const CashFlowExpense: React.FC<{
  cashFlowData: any;
  isForecastOnly: boolean;
  year: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
}> = ({ cashFlowData, isForecastOnly, year, isEditable, setIsEditable }) => {
  const [transformedData, setTransformedData] = useState<ICashFlowExpense[]>([]);
  const currentYear = "20" + getCurrentFinancialYear();
  const [transformedFooter, setTransformedFooter] = useState<ICashFlowExpenseSummary[]>([]);
  const [collapsedRows, setCollapsedRows] = useState<Record<string, boolean>>({});
  const { isShowingTotals } = useFilterSelection();
  const navigate = useNavigate();

  useEffect(() => {
    if (cashFlowData && Array.isArray(cashFlowData.PropertyDetails) && cashFlowData.PropertyDetails.length > 0) {
      let groupCounter: number = -1;
      let lastGroupKey: string = "";
      const transformed = cashFlowData.PropertyDetails.map((properties: any) => {
        const groupKey = properties.PropertyDetail.StreetAddress;
        if (groupKey !== lastGroupKey) {
          groupCounter++;
          lastGroupKey = groupKey;
        }
        const actual = isShowingTotals
          ? properties.Details.CashflowPropertyExpenseTotal?.Actuals?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowPropertyExpensePortion?.Actuals?.find((item: any) => item.Year === year) || {};
        const forecast = isShowingTotals
          ? properties.Details.CashflowPropertyExpenseTotal?.Forecast?.find((item: any) => item.Year === year) || {}
          : properties.Details.CashflowPropertyExpensePortion?.Forecast?.find((item: any) => item.Year === year) || {};
        const variance =
          Number(year) <= Number(currentYear)
            ? isShowingTotals
              ? properties.Details.CashflowPropertyExpenseTotal?.Variance?.find((item: any) => item.Year === year) || {}
              : properties.Details.CashflowPropertyExpensePortion?.Variance?.find((item: any) => item.Year === year) ||
                {}
            : {};

        return [
          { type: "Forecast", data: forecast },
          { type: "Actuals", data: actual },
          { type: "Variance", data: variance },
        ].map(({ type, data }) => ({
          street: properties.PropertyDetail?.StreetAddress || "",
          suburb: properties.PropertyDetail?.Suburb || "",
          state: properties.PropertyDetail?.State || "",
          postcode: properties.PropertyDetail?.Postcode || "",
          country: properties.PropertyDetail?.Country || "",
          type,
          propertyManagment: Number(data.PropertyMgmt) || 0,
          serviceContranct: Number(data.ServiceCont) || 0,
          rates: Number(data.Rates) || 0,
          bodyCorp: Number(data.BodyCorp) || 0,
          insurance: Number(data.Insurance) || 0,
          repairs: Number(data.Repairs) || 0,
          landLease: Number(data.LandLease) || 0,
          garden: Number(data.GardenFee) || 0,
          cleaning: Number(data.Cleaning) || 0,
          other: Number(data.Other) || 0,
          propertyId: properties.PropertyDetail.PortfolioTrackerId,
          groupIndex: groupCounter % 2,
        }));
      }).flat();

      setTransformedData(transformed);

      const footerProperties = cashFlowData.TotalForDetails.find((item: any) => item.EntityId === "") || {};
      const actualFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.PropertyExpense?.Actuals?.find((item: any) => item.Year === year) || {}
        : footerProperties.CashflowDetailsPortions.PropertyExpense?.Actuals?.find((item: any) => item.Year === year) ||
          {};
      const forecastFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.PropertyExpense?.Forecast?.find((item: any) => item.Year === year) ||
          {}
        : footerProperties.CashflowDetailsPortions.PropertyExpense?.Forecast?.find((item: any) => item.Year === year) ||
          {};
      const varianceFooter = isShowingTotals
        ? footerProperties.CashflowDetailsTotals.PropertyExpense?.Variance?.find((item: any) => item.Year === year) ||
          {}
        : footerProperties.CashflowDetailsPortions.PropertyExpense?.Variance?.find((item: any) => item.Year === year) ||
          {};

      const footer = [
        {
          type: "Actuals",
          propertyManagment: Number(actualFooter.PropertyMgmt) || 0,
          serviceContranct: Number(actualFooter.ServiceCont) || 0,
          rates: Number(actualFooter.Rates) || 0,
          bodyCorp: Number(actualFooter.BodyCorp) || 0,
          insurance: Number(actualFooter.Insurance) || 0,
          repairs: Number(actualFooter.Repairs) || 0,
          landLease: Number(actualFooter.LandLease) || 0,
          garden: Number(actualFooter.GardenFee) || 0,
          cleaning: Number(actualFooter.Cleaning) || 0,
          other: Number(actualFooter.Other) || 0,
        },
        {
          type: "Forecast",
          propertyManagment: Number(forecastFooter.PropertyMgmt) || 0,
          serviceContranct: Number(forecastFooter.ServiceCont) || 0,
          rates: Number(forecastFooter.Rates) || 0,
          bodyCorp: Number(forecastFooter.BodyCorp) || 0,
          insurance: Number(forecastFooter.Insurance) || 0,
          repairs: Number(forecastFooter.Repairs) || 0,
          landLease: Number(forecastFooter.LandLease) || 0,
          garden: Number(forecastFooter.GardenFee) || 0,
          cleaning: Number(forecastFooter.Cleaning) || 0,
          other: Number(forecastFooter.Other) || 0,
        },
        {
          type: "Variance",
          propertyManagment: Number(varianceFooter.PropertyMgmt) || 0,
          serviceContranct: Number(varianceFooter.ServiceCont) || 0,
          rates: Number(varianceFooter.Rates) || 0,
          bodyCorp: Number(varianceFooter.BodyCorp) || 0,
          insurance: Number(varianceFooter.Insurance) || 0,
          repairs: Number(varianceFooter.Repairs) || 0,
          landLease: Number(varianceFooter.LandLease) || 0,
          garden: Number(varianceFooter.GardenFee) || 0,
          cleaning: Number(varianceFooter.Cleaning) || 0,
          other: Number(varianceFooter.Other) || 0,
        },
      ];

      setTransformedFooter(footer);
    }
  }, [cashFlowData, year, isShowingTotals]);

  const currencyTemplate = (value: number) => (
    <span className={value < 0 ? "text-red-600" : ""}>
      {value < 0 ? `(${formatCurrency(-value)})` : formatCurrency(value)}
    </span>
  );

  const renderFooter = (category: any, data: ICashFlowExpenseSummary) =>
    transformedFooter && (
      <Row>
        <Column footer={category == "Forecast" ? "Totals/averages" : ""} />
        <Column field="type" footer={category} />
        <Column footer={currencyTemplate(data?.propertyManagment)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.serviceContranct)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.rates)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.bodyCorp)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.insurance)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.repairs)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.landLease)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.garden)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.cleaning)} footerStyle={{ textAlign: "right" }} />
        <Column footer={currencyTemplate(data?.other)} footerStyle={{ textAlign: "right" }} />
      </Row>
    );

  const footerGroup =
    transformedFooter &&
    (Number(year) > Number(currentYear) ? (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Actual"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
        {!isForecastOnly && (
          <Row>
            <Column footer={""} />
            <Column footer={"Variance"} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
            <Column footer={""} />
          </Row>
        )}
      </ColumnGroup>
    ) : (
      <ColumnGroup>
        {renderFooter("Forecast", transformedFooter.find((x) => x.type == "Forecast")!)}
        {!isForecastOnly && renderFooter("Actuals", transformedFooter.find((x) => x.type == "Actuals")!)}
        {!isForecastOnly && renderFooter("Variance", transformedFooter.find((x) => x.type == "Variance")!)}
      </ColumnGroup>
    ));

  const toggleRowCollapse = (street: string) => {
    setCollapsedRows((prev) => ({ ...prev, [street]: !prev[street] }));
  };

  const renderCell = (rowData: any, field: string, templateFn: (value: number) => JSX.Element) => {
    const shouldRender = isForecastOnly
      ? collapsedRows[rowData.street] || rowData.type === "Forecast"
      : !collapsedRows[rowData.street] || rowData.type === "Forecast";

    if (
      Number(year) > Number(currentYear) &&
      rowData.type !== "Forecast" &&
      rowData[field] !== "Actuals" &&
      rowData[field] !== "Variance"
    ) {
      return null;
    }
    return shouldRender ? templateFn(rowData[field]) : null;
  };

  return isEditable ? (
    <EditExpense cashFlowData={cashFlowData} setIsEditable={setIsEditable} />
  ) : (
    transformedData && transformedFooter && (
      <DataTable
        value={transformedData}
        rowGroupMode="rowspan"
        groupRowsBy="street"
        sortOrder={1}
        size="small"
        footerColumnGroup={footerGroup}
        rowClassName={(rowData) => (rowData.groupIndex === 1 ? "groupRowGrey" : "")}
      >
        <Column
          field="street"
          header="Address/property name"
          body={(rowData) => (
            <div className="flex items-center">
              {isForecastOnly && (
                <REIButton
                  id={`mcfexp-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-down" : "pi pi-angle-right"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              {!isForecastOnly && (
                <REIButton
                  id={`mcfexp-btn-collapse-${rowData.propertyId}`}
                  icon={collapsedRows[rowData.street] ? "pi pi-angle-right" : "pi pi-angle-down"}
                  text={true}
                  onClick={() => toggleRowCollapse(rowData.street)}
                />
              )}
              <span
                id={`mcfexp-lnk-address-${rowData.propertyId}`}
                data-testid={`mcfexp-lnk-address-${rowData.propertyId}`}
                onClick={() => {
                  navigate(`/portfolio-tracker`, {
                    state: {
                      tab: "MyPropertyTab",
                      MyPropertyId: rowData.propertyId,
                    },
                  });
                }}
                className="cursor-pointer text-blue-700"
              >{`${rowData.street}, ${rowData.suburb}, ${rowData.state}, ${rowData.postcode} (${rowData.country})`}</span>
            </div>
          )}
        />
        <Column field="type" header="Type" body={(rowData) => renderCell(rowData, "type", (value) => value)} />

        <Column
          field="propertyManagment"
          header="Property mngmt."
          body={(rowData) => renderCell(rowData, "propertyManagment", currencyTemplate)}
          align="right"
        />
        <Column
          field="serviceContranct"
          header="Service contracts"
          body={(rowData) => renderCell(rowData, "serviceContranct", currencyTemplate)}
          align="right"
        />
        <Column
          field="rates"
          header="Rates"
          body={(rowData) => renderCell(rowData, "rates", currencyTemplate)}
          align="right"
        />
        <Column
          field="bodyCorp"
          header="Body corp."
          body={(rowData) => renderCell(rowData, "bodyCorp", currencyTemplate)}
          align="right"
        />
        <Column
          field="insurance"
          header="Insurance"
          body={(rowData) => renderCell(rowData, "insurance", currencyTemplate)}
          align="right"
        />
        <Column
          field="repairs"
          header="Repairs"
          body={(rowData) => renderCell(rowData, "repairs", currencyTemplate)}
          align="right"
        />
        <Column
          field="landLease"
          header="Land lease"
          body={(rowData) => renderCell(rowData, "landLease", currencyTemplate)}
          align="right"
        />
        <Column
          field="garden"
          header="Garden"
          body={(rowData) => renderCell(rowData, "garden", currencyTemplate)}
          align="right"
        />
        <Column
          field="cleaning"
          header="Cleaning"
          body={(rowData) => renderCell(rowData, "cleaning", currencyTemplate)}
          align="right"
        />
        <Column
          field="other"
          header="Other"
          body={(rowData) => renderCell(rowData, "other", currencyTemplate)}
          align="right"
        />
      </DataTable>
    )
  );
};

export default CashFlowExpense;
